/**
 * Development-only instrumentation for `createClient({ global: { fetch } })`.
 * Records every fetch invoked by the Supabase browser client (PostgREST, Auth, etc.).
 */

/** @typedef {{ ts: number, phase: 'start' | 'response' | 'error', method: string, url: string, status?: number, errorName?: string, errorMessage?: string, elapsedMs?: number, correlation?: string | null }} SupabaseFetchDebugEvent */

const IS_DEV = process.env.NODE_ENV === 'development';
const MAX_EVENTS = 120;
/** @type {SupabaseFetchDebugEvent[]} */
const events = [];

/** Optional label (e.g. saveClient) so console lines can be tied to app code paths. */
let activeFetchCorrelation = null;

/** @param {string | null | undefined} id */
export function setActiveFetchCorrelation(id) {
  activeFetchCorrelation = typeof id === 'string' && id ? id : null;
}

export function clearActiveFetchCorrelation() {
  activeFetchCorrelation = null;
}

export function clearSupabaseFetchDebugEvents() {
  events.length = 0;
}

/** @returns {SupabaseFetchDebugEvent[]} */
export function getSupabaseFetchDebugEvents() {
  return events.slice();
}

function pushEvent(ev) {
  events.push(ev);
  while (events.length > MAX_EVENTS) events.shift();
}

function requestUrl(input) {
  try {
    if (typeof input === 'string') return input;
    if (input instanceof URL) return input.href;
    if (input && typeof input === 'object' && 'url' in input) return String(/** @type {Request} */ (input).url);
  } catch {
    /* ignore */
  }
  return String(input);
}

function requestMethod(input, init) {
  if (init && init.method) return String(init.method).toUpperCase();
  if (input && typeof input === 'object' && !(input instanceof URL) && 'method' in input) {
    const m = /** @type {Request} */ (input).method;
    return m ? String(m).toUpperCase() : 'GET';
  }
  return 'GET';
}

/**
 * Wrap native fetch; log start / response status / thrown errors (incl. AbortError).
 * @param {typeof fetch} baseFetch
 * @returns {typeof fetch}
 */
export function createWrappedFetchForSupabaseDebug(baseFetch) {
  return async (input, init) => {
    const t0 = performance.now();
    const url = requestUrl(input);
    const method = requestMethod(input, init);
    const urlShort = url.slice(0, 800);
    const correlation = activeFetchCorrelation;
    pushEvent({ ts: performance.now(), phase: 'start', method, url: urlShort, correlation });
    if (IS_DEV) {
      // eslint-disable-next-line no-console
      console.log('[SUPABASE_GLOBAL_FETCH] start', correlation || '—', method, urlShort.slice(0, 240));
    }

    try {
      const res = await baseFetch(input, init);
      const elapsedMs = Math.round(performance.now() - t0);
      pushEvent({
        ts: performance.now(),
        phase: 'response',
        method,
        url: urlShort.slice(0, 400),
        status: res.status,
        elapsedMs,
        correlation,
      });
      if (IS_DEV) {
        // eslint-disable-next-line no-console
        console.log('[SUPABASE_GLOBAL_FETCH] response', correlation || '—', method, res.status, `${elapsedMs}ms`, urlShort.slice(0, 160));
      }
      return res;
    } catch (err) {
      const elapsedMs = Math.round(performance.now() - t0);
      pushEvent({
        ts: performance.now(),
        phase: 'error',
        method,
        url: urlShort.slice(0, 400),
        errorName: /** @type {{ name?: string }} */ (err).name,
        errorMessage: String(/** @type {{ message?: string }} */ (err).message || err),
        elapsedMs,
        correlation,
      });
      if (IS_DEV) {
        // eslint-disable-next-line no-console
        console.log(
          '[SUPABASE_GLOBAL_FETCH] error',
          correlation || '—',
          method,
          /** @type {{ name?: string }} */ (err).name,
          String(/** @type {{ message?: string }} */ (err).message || err),
          `${elapsedMs}ms`
        );
      }
      throw err;
    }
  };
}

/** Events from this wrapper whose URL looks like PostgREST `profiles`. */
export function filterProfilesRestEvents(list) {
  return list.filter((e) => /\/rest\/v1\/profiles\b/i.test(e.url));
}
